import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const IRS_RATE = 0.70;

export default function Reports() {
  const { currentUser } = useAuth();
  const [trips, setTrips] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState('drives');

  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [purposeFilter, setPurposeFilter] = useState('all');

  useEffect(() => {
    async function fetchData() {
      try {
        const [tripsSnap, expensesSnap] = await Promise.all([
          getDocs(query(collection(db, 'users', currentUser.uid, 'trips'), orderBy('date', 'desc'))),
          getDocs(query(collection(db, 'users', currentUser.uid, 'expenses'), orderBy('date', 'desc'))),
        ]);
        setTrips(tripsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setExpenses(expensesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching report data:', err);
      }
      setLoading(false);
    }
    fetchData();
  }, [currentUser.uid]);

  function filterByDate(items) {
    return items.filter((item) => {
      const d = item.date;
      if (!d) return false;
      return d >= dateFrom && d <= dateTo;
    });
  }

  const filteredTrips = filterByDate(trips).filter(
    (t) => purposeFilter === 'all' || t.purpose === purposeFilter
  );

  const filteredExpenses = filterByDate(expenses).filter(
    (e) => categoryFilter === 'all' || e.category === categoryFilter
  );

  const tripPurposes = [...new Set(trips.map((t) => t.purpose).filter(Boolean))];
  const expenseCategories = [...new Set(expenses.map((e) => e.category).filter(Boolean))];

  const totalMiles = filteredTrips.reduce((sum, t) => sum + (t.distance || 0), 0);
  const totalDeduction = filteredTrips.reduce((sum, t) => sum + (t.deduction || t.distance * IRS_RATE || 0), 0);
  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.total || 0), 0);

  function exportDrivesCSV() {
    const headers = ['Date', 'From', 'To', 'Purpose', 'Miles', 'Deduction'];
    const rows = filteredTrips.map((t) => [
      t.date,
      `"${(t.startAddress || '').replace(/"/g, '""')}"`,
      `"${(t.endAddress || '').replace(/"/g, '""')}"`,
      t.purpose || 'Business',
      (t.distance || 0).toFixed(1),
      (t.deduction || t.distance * IRS_RATE || 0).toFixed(2),
    ]);
    downloadCSV(headers, rows, `milededuct-drives-${dateFrom}-to-${dateTo}.csv`);
  }

  function exportExpensesCSV() {
    const headers = ['Date', 'Merchant', 'Category', 'Amount', 'Notes'];
    const rows = filteredExpenses.map((e) => [
      e.date,
      `"${(e.merchant || '').replace(/"/g, '""')}"`,
      e.category || '',
      (e.total || 0).toFixed(2),
      `"${(e.notes || '').replace(/"/g, '""')}"`,
    ]);
    downloadCSV(headers, rows, `milededuct-expenses-${dateFrom}-to-${dateTo}.csv`);
  }

  function downloadCSV(headers, rows, filename) {
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return <div className="screen-container"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="screen-container">
      <h2>Reports</h2>

      {/* Report Type Toggle */}
      <div className="report-tabs">
        <button
          className={`report-tab ${activeReport === 'drives' ? 'active' : ''}`}
          onClick={() => setActiveReport('drives')}
        >
          Drives
        </button>
        <button
          className={`report-tab ${activeReport === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveReport('expenses')}
        >
          Expenses
        </button>
      </div>

      {/* Date Range */}
      <div className="report-date-range">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      {/* Quick Date Presets */}
      <div className="report-presets">
        {[
          { label: 'This Month', fn: () => { const now = new Date(); setDateFrom(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`); setDateTo(now.toISOString().split('T')[0]); }},
          { label: 'Last Month', fn: () => { const now = new Date(); const prev = new Date(now.getFullYear(), now.getMonth()-1, 1); const end = new Date(now.getFullYear(), now.getMonth(), 0); setDateFrom(prev.toISOString().split('T')[0]); setDateTo(end.toISOString().split('T')[0]); }},
          { label: 'This Year', fn: () => { const now = new Date(); setDateFrom(`${now.getFullYear()}-01-01`); setDateTo(now.toISOString().split('T')[0]); }},
          { label: 'Last Year', fn: () => { const yr = new Date().getFullYear()-1; setDateFrom(`${yr}-01-01`); setDateTo(`${yr}-12-31`); }},
        ].map((p) => (
          <button key={p.label} className="preset-chip" onClick={p.fn}>{p.label}</button>
        ))}
      </div>

      {activeReport === 'drives' ? (
        <>
          {/* Purpose Filter */}
          {tripPurposes.length > 0 && (
            <div className="report-filter">
              <select value={purposeFilter} onChange={(e) => setPurposeFilter(e.target.value)}>
                <option value="all">All Purposes</option>
                {tripPurposes.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}

          {/* Drive Summary */}
          <div className="report-summary">
            <div className="summary-card">
              <span className="summary-value">{filteredTrips.length}</span>
              <span className="summary-label">Trips</span>
            </div>
            <div className="summary-card">
              <span className="summary-value">{totalMiles.toFixed(1)}</span>
              <span className="summary-label">Miles</span>
            </div>
            <div className="summary-card">
              <span className="summary-value" style={{ color: '#34D399' }}>${totalDeduction.toFixed(2)}</span>
              <span className="summary-label">Deductions</span>
            </div>
          </div>

          {/* Drive List */}
          <div className="report-list">
            {filteredTrips.length === 0 ? (
              <div className="empty-state"><p>No drives in this date range.</p></div>
            ) : (
              filteredTrips.map((trip) => (
                <div key={trip.id} className="report-row">
                  <div className="report-row-left">
                    <span className="report-row-date">
                      {new Date(trip.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="report-row-detail">{trip.startAddress || 'Start'} &rarr; {trip.endAddress || 'End'}</span>
                  </div>
                  <div className="report-row-right">
                    <span className="report-row-value">{(trip.distance || 0).toFixed(1)} mi</span>
                    <span className="report-row-sub">${(trip.deduction || trip.distance * IRS_RATE || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {filteredTrips.length > 0 && (
            <button className="btn btn-primary report-export-btn" onClick={exportDrivesCSV}>
              Export Drives CSV
            </button>
          )}
        </>
      ) : (
        <>
          {/* Category Filter */}
          {expenseCategories.length > 0 && (
            <div className="report-filter">
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="all">All Categories</option>
                {expenseCategories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {/* Expense Summary */}
          <div className="report-summary">
            <div className="summary-card">
              <span className="summary-value">{filteredExpenses.length}</span>
              <span className="summary-label">Expenses</span>
            </div>
            <div className="summary-card">
              <span className="summary-value" style={{ color: '#34D399' }}>${totalExpenses.toFixed(2)}</span>
              <span className="summary-label">Total</span>
            </div>
          </div>

          {/* Expense List */}
          <div className="report-list">
            {filteredExpenses.length === 0 ? (
              <div className="empty-state"><p>No expenses in this date range.</p></div>
            ) : (
              filteredExpenses.map((exp) => (
                <div key={exp.id} className="report-row">
                  <div className="report-row-left">
                    <span className="report-row-date">
                      {new Date(exp.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="report-row-detail">{exp.merchant || 'Unknown'} &middot; {exp.category}</span>
                  </div>
                  <div className="report-row-right">
                    <span className="report-row-value">${(exp.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {filteredExpenses.length > 0 && (
            <button className="btn btn-primary report-export-btn" onClick={exportExpensesCSV}>
              Export Expenses CSV
            </button>
          )}
        </>
      )}
    </div>
  );
}
